import React, {
  SetStateAction,
  Dispatch,
  createContext,
  PropsWithChildren
} from 'react';
import { LabeledItemsGroup, TreeViewSelect } from '@core/components';
import { TreeViewSelectProps } from '@core/components/src/TreeViewSelect/TreeViewSelect';
import {
  SelectBox,
  NumberBox,
  TextBox,
  CheckBox,
  TagBox,
  TextArea
} from 'devextreme-react';
import { ISelectBoxOptions } from 'devextreme-react/select-box';
import { ITextBoxOptions } from 'devextreme-react/text-box';
import { ICheckBoxOptions } from 'devextreme-react/check-box';
import { INumberBoxOptions } from 'devextreme-react/number-box';
import { ITagBoxOptions } from 'devextreme-react/tag-box';
import {
  formItemHocSetup,
  formItemNoLabelHocSetup
} from './utils/formItemHocSetup';
import AdditionSelect, {
  AdditionSelectProps
} from '../AdditionSelect/AdditionSelect';
import { useTranslation } from '@core/hooks';
import { ITextAreaOptions } from 'devextreme-react/text-area';

export type TagBoxValueExpressions = string[] | number[];

export type FieldValue =
  | TagBoxValueExpressions
  | string
  | number
  | null
  | undefined;

interface FormValue {
  [fieldName: string]: FieldValue;
}

type CustomValCb = (fieldName: string, value: FieldValue) => void;

type SimpleFormProps<TData> = {
  formValue: TData;
} & (
  | {
      setFormValue: Dispatch<SetStateAction<TData>>;
      useCustomValCb?: never;
    }
  | {
      setFormValue: CustomValCb;
      useCustomValCb: true;
    }
);

export interface SimpleFormContextValue {
  setFormItemValue: (name: string, value: FieldValue) => void;
  getFormItemValue: (fieldName: string) => FieldValue;
}

export interface RelativeField {
  local: string;
  relative: string;
}

export const SimpleFormContext = createContext<SimpleFormContextValue>(
  {} as SimpleFormContextValue
);

function SimpleForm<TData = void>({
  formValue,
  setFormValue,
  useCustomValCb,
  children
}: PropsWithChildren<SimpleFormProps<TData>>) {
  const getFormItemValue = (fieldName: string) => {
    const val = ((formValue as unknown) as FormValue)[fieldName];

    return val;
  };

  const setFormItemValue = (fieldName: string, value: FieldValue) => {
    if (useCustomValCb) {
      const cb = setFormValue as CustomValCb;

      cb(fieldName, value);

      return;
    }

    const cb = setFormValue as Dispatch<SetStateAction<TData>>;

    cb((prevState: any) => ({ ...prevState, [fieldName]: value }));
  };

  return (
    <div className="simple-form-wrapper">
      <LabeledItemsGroup>
        <SimpleFormContext.Provider
          value={{ getFormItemValue, setFormItemValue }}
        >
          {children}
        </SimpleFormContext.Provider>
      </LabeledItemsGroup>
    </div>
  );
}

type InternalAditionSelectProps = Omit<
  AdditionSelectProps<any>,
  'recordId' | 'setRecordId' | 'showLabel' | 'popupTitle'
>;

const AdditionSelectInternal = (props: any) => {
  const { value } = props;

  const setRecordId = (id: number) => {
    props.onValueChanged({ value: id });
  };

  const { t } = useTranslation();

  return (
    <AdditionSelect
      {...props}
      popupTitle={t('Edycja')}
      showLabel={false}
      recordId={value}
      setRecordId={setRecordId}
    />
  );
};

export const SimpleFormItem = {
  SelectBox: formItemHocSetup<
    ISelectBoxOptions & { relativeFields?: RelativeField[] }
  >(SelectBox),
  TagBox: formItemHocSetup<ITagBoxOptions>(TagBox),
  TextBox: formItemHocSetup<ITextBoxOptions>(TextBox),
  NumberBox: formItemHocSetup<INumberBoxOptions>(NumberBox),
  TextArea: formItemHocSetup<ITextAreaOptions>(TextArea),
  CheckBox: formItemHocSetup<ICheckBoxOptions>((props: any) => (
    <div>
      <CheckBox {...props} />
    </div>
  )),
  TreeViewSelect: formItemHocSetup<TreeViewSelectProps>((props: any) => (
    <TreeViewSelect {...props} />
  )),
  AdditionSelect: formItemHocSetup<InternalAditionSelectProps>(
    AdditionSelectInternal
  )
};

export const SimpleFormItemNoLabel = {
  SelectBox: formItemNoLabelHocSetup<
    ISelectBoxOptions & { relativeFields?: RelativeField[] }
  >(SelectBox),
  TagBox: formItemNoLabelHocSetup<ITagBoxOptions>(TagBox),
  TextBox: formItemNoLabelHocSetup<ITextBoxOptions>(TextBox),
  NumberBox: formItemNoLabelHocSetup<INumberBoxOptions>(NumberBox),
  CheckBox: formItemNoLabelHocSetup<ICheckBoxOptions>(CheckBox),
  TreeViewSelect: formItemNoLabelHocSetup<TreeViewSelectProps>((props: any) => (
    <TreeViewSelect {...props} />
  )),
  AdditionSelect: formItemNoLabelHocSetup<InternalAditionSelectProps>(
    AdditionSelectInternal
  )
};

export default SimpleForm;
